import { Building2, Users, ArrowLeft, Handshake, Home, BookOpen, PlugZap, MailCheck, Reply, MailX, Globe } from 'lucide-preact';
import { LAYOUT_CONSTANTS } from '../../constants/layout';
import { THEME_COLORS } from '../../constants/theme';
import { useTheme } from '../../hooks/useTheme';
import { useUserProfile, USER_ROLES } from '../../hooks/useUserProfile';
import { useLocation } from 'preact-iso'
import { useManagesCustomers } from '../console/admin/useManagesCustomers'

interface SettingsNavigationProps {
  activeSection: string
  onSectionChange: (section: string) => void
  onBackToApp: () => void
  isOpen?: boolean
  onToggle?: () => void
}

export function SettingsNavigation({
  activeSection,
  onSectionChange,
  onBackToApp,
  isOpen = true,
  onToggle
}: SettingsNavigationProps) {
  const { resolvedTheme } = useTheme()
  const { getCurrentCompanyRoleSet, hasCompanyRole, canManageCustomers, homeClientCompanyId } = useUserProfile()
  const location = useLocation()
  const assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL

  // Get current company ID from URL
  const currentCompanyId = (() => {
    try {
      const match = location?.path?.match(/\/company\/([^/]+)/)
      return match?.[1] || null
    } catch {
      return null
    }
  })()
  // ⚑ Not the route company's role set: it is looked up by the route company, so comparing against it always matched.
  const managesCustomers = useManagesCustomers(currentCompanyId ?? '')

  const userRoles = getCurrentCompanyRoleSet()

  // Check if user is a company admin or has admin role in any workspace
  const canAccessUsers = () => {
    if (!userRoles || !currentCompanyId) return false

    // Check if user is a company admin
    if (userRoles.IsAdmin || hasCompanyRole(USER_ROLES.ADMIN, currentCompanyId)) {
      return true
    }

    // Check if user is admin in any workspace (using CanAdmin flag)
    return userRoles.WorkspaceRoles?.some(wsRole => wsRole.CanAdmin)
  }


  // Use different logos based on theme
  const logoUrl = resolvedTheme === 'dark'
    ? `${assetsBaseUrl}/assets/white-orbital-x.png`
    : `${assetsBaseUrl}/assets/black-orbital-x.png`

  const companyNavigationItems = [
    { id: 'settings/company', label: 'Company', icon: Building2 },
  ]

  if (managesCustomers) {
    companyNavigationItems.push({ id: 'settings/customers', label: 'Customers', icon: Handshake })
  }

  // Add Authentication if user can manage customers
  if (canManageCustomers) {
    companyNavigationItems.push({ id: 'settings/authentication', label: 'Authentication', icon: Building2 })
  }

  // Add Users if user has admin permissions (company or any workspace)
  if (canAccessUsers()) {
    companyNavigationItems.push({ id: 'settings/users', label: 'Users', icon: Users })
  }

  // Knowledge Base is always available in admin settings
  companyNavigationItems.push({ id: 'settings/knowledge-base', label: 'Knowledge Base', icon: BookOpen })

  // Integrations — company admins only
  if (userRoles?.IsAdmin) {
    companyNavigationItems.push({ id: 'settings/integrations', label: 'Integrations', icon: PlugZap })
  }

  // Sending Domains — company admins only
  if (userRoles?.IsAdmin) {
    companyNavigationItems.push({ id: 'settings/sending-domains', label: 'Sending Domains', icon: MailCheck })
    companyNavigationItems.push({ id: 'settings/non-prospect-domains', label: 'Non-Prospect Domains', icon: Globe })
  }

  // Reply-To Addresses — company admins only
  if (userRoles?.IsAdmin) {
    companyNavigationItems.push({ id: 'settings/reply-to-addresses', label: 'Reply-To Addresses', icon: Reply })
  }

  // Suppression (addresses + domains) — company admins only
  if (userRoles?.IsAdmin) {
    companyNavigationItems.push({ id: 'settings/suppression-list', label: 'Suppression', icon: MailX })
  }

  const addBackToHome = Boolean(canManageCustomers && homeClientCompanyId && currentCompanyId && homeClientCompanyId !== currentCompanyId)

  const handleBackToHome = () => {
    if (homeClientCompanyId) {
      location.route(`/company/${homeClientCompanyId}`)
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onToggle}
        />
      )}

      {/* Navigation sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50
        w-64
        bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-r border-slate-200 dark:border-slate-700
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-screen
      `}>
        {/* Logo section */}
        <div
          className="p-4 flex items-center justify-center"
          style={{ height: `${LAYOUT_CONSTANTS.TOP_NAV_HEIGHT}px` }}
        >
          <div className="flex items-center">
            <img
              src={logoUrl}
              alt="OrbitalX"
              className="h-[60px] w-auto"
              onError={(e) => {
                console.error('Failed to load logo from:', logoUrl)
                // Fallback to text if image fails to load
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling?.classList.remove('hidden')
              }}
            />
            {/* Fallback text logo */}
            <span className="hidden text-xl font-bold">OrbitalX</span>
          </div>
        </div>

        {/* Back to App + Back to Home Company */}
        <div className="px-4 pb-4 space-y-2">
          <button
            onClick={() => {
              onBackToApp()
              // Close mobile menu when button is clicked
              if (onToggle && window.innerWidth < 1024) {
                onToggle()
              }
            }}
            className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors duration-200 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700"
            title="Back to App"
          >
            <ArrowLeft className="mr-3 w-4 h-4" />
            <span className="text-sm font-medium">Back to App</span>
          </button>

          {addBackToHome && (
            <button
              onClick={() => {
                handleBackToHome()
                if (onToggle && window.innerWidth < 1024) {
                  onToggle()
                }
              }}
              className="w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors duration-200 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-slate-700"
              title="Back to Home Company"
            >
              <Home className="mr-3 w-4 h-4" />
              <span className="text-sm font-medium">Back to Home Company</span>
            </button>
          )}
        </div>

        {/* Admin Header */}
        <div className="px-4 pb-2">
          <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Admin
          </h2>
        </div>

        {/* Company Navigation items */}
        <nav className="flex-1 overflow-y-auto px-4">
          <ul className="space-y-2">
            {companyNavigationItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => {
                    onSectionChange(item.id)
                    // Close mobile menu when item is selected
                    if (onToggle && window.innerWidth < 1024) {
                      onToggle()
                    }
                  }}
                  className={`
                    w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors duration-200
                    ${activeSection === item.id
                      ? 'text-slate-900 dark:text-slate-100 border'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    }
                  `}
                  style={{
                    backgroundColor: activeSection === item.id ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent',
                    borderColor: activeSection === item.id ? THEME_COLORS.BORDERS.LIGHT : 'transparent'
                  }}
                  title={item.label}
                >
                  <item.icon
                    className="mr-3 w-5 h-5 transition-colors duration-200"
                    style={{ color: activeSection === item.id ? THEME_COLORS.GREEN[500] : 'inherit' }}
                  />
                  <span
                    className="text-sm font-medium transition-colors duration-200"
                    style={{ color: activeSection === item.id ? THEME_COLORS.GREEN[600] : 'inherit' }}
                  >
                    {item.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer with version - always visible at bottom */}
        <div className="flex-shrink-0 p-2 border-t border-slate-200 dark:border-slate-700">
          <div className="text-xs text-slate-400 dark:text-slate-400 text-center">
            v{import.meta.env.VITE_APP_VERSION || '1.0'}
          </div>
        </div>
      </div>
    </>
  )
}
